import { useEffect, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check, Loader2, PauseCircle } from 'lucide-react';
import { toast } from 'sonner';
import { PLAN_FEATURES } from '@scio3/shared-types';
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Textarea,
} from '@scio3/ui';
import {
  ALL_PLAN_FEATURES_QUERY,
  CANCEL_SUBSCRIPTION_V2,
  GET_UPCOMING_SUBSCRIPTION,
  SEND_CANCELLATION_SUPPORT_REQUEST,
  SUBMIT_USER_SURVEY,
  type SendCancellationSupportRequestVariables,
  type SubmitUserSurveyVariables,
} from '@scio3/graphql-client';
import { useDialogSubmitState } from '@/hooks/useDialogSubmitState';
import { formatDateMedium } from '@/lib/format-date';
import { useBillingData } from '../hooks/useBillingData';
import { useBillingDialogStore } from '../stores/billing-dialog-store';
import { getCurrencySymbol, getPlanDisplayName } from '../utils/plan-helpers';

const ADDITIONAL_INFO_MAX = 500;

/**
 * Help-article link shown on the special-pricing step — putting devices On
 * Hold (no license consumed) is a cheaper alternative to cancelling for
 * seasonal users.
 */
const ON_HOLD_HELP_URL = 'https://links.optisigns.com/4dmEfq5';

type Props = {
  open: boolean;
  onClose: (submitted?: boolean) => void;
  subAccountId?: string;
  /**
   * Handoff to the change-plan wizard step. Fired when the user picks
   * "I want to switch plans" and confirms — the parent closes this dialog
   * and navigates the SubscriptionWizard to STEP.PLAN.
   */
  onSwitchPlans?: () => void;
  /**
   * Handoff to the wizard's plan-switch flow (skips the regular plan-change
   * warning and jumps to the Confirmation step). Fired when the user picks
   * "It's too expensive" and accepts the retention offer for a paid tier.
   *
   * The target plan is **dynamic** — despite the prop name, the actual
   * destination depends on the user's current plan:
   *   - Engage   → Pro Plus
   *   - Pro Plus → Standard
   * (Standard → Free is routed through `onSwitchToFree` instead.)
   *
   * The parent must forward `targetPlanAlias` to its plan-switch handler;
   * hardcoding STANDARD here would put Engage users on the wrong plan. The
   * name should probably be renamed to `onAcceptPaidRetention` in a future
   * cleanup MR.
   */
  onSwitchToStandard?: (targetPlanAlias: string) => void;
  /**
   * Handoff to the wizard's downgrade-to-Free flow. Fired when the user picks
   * "It's too expensive" while already on Standard — Free is the only cheaper
   * tier left — and accepts the "Switch to Free" retention offer. Parent
   * should call the same handler used by the plan-step "Select Free" button
   * (opens DowngradeSurveyDialog).
   */
  onSwitchToFree?: () => void;
  /**
   * Called when the user backs out via any "keep" button — Survey-step
   * "Keep current plan" / "Go Back" or Confirm-step "Keep my current
   * subscription". Parents (e.g. SubscriptionWizardDialog) should close
   * any wrapping dialogs and return the user to the Subscription page.
   * When omitted the dialog just closes itself.
   */
  onKeepSubscription?: () => void;
};

/**
 * Multi-step flow:
 *  - `survey` — reason picker (radio list)
 *  - `switch-to-standard` — retention offer for "too expensive" users not
 *     already on Standard
 *  - `special-pricing` — retention offer for "only seasonal / don't use it
 *     enough" users; captures their use case to forward to the sales team
 *  - `having-trouble` — retention offer for "Trouble understanding product"
 *     users; captures their issue and offers a 1:1 follow-up email
 *  - `thank-you` — shared post-submit acknowledgement for the two
 *     "request a follow-up" retention paths above
 *
 * The final "are you sure" gate is NOT a step — it renders as a separate
 * dialog stacked on top of whichever step the user was on (see `confirmOpen`).
 */
type Step = 'survey' | 'switch-to-standard' | 'special-pricing' | 'having-trouble' | 'thank-you';

/**
 * Standard plan feature bullets shown on the retention offer. Hardcoded for
 * now to match design; could later be pulled from PLAN_FEATURES_QUERY.
 */
const STANDARD_FEATURE_KEYS: string[] = [
  'Image, Video, Document',
  'Templates & Design Tool',
  'Playlist, Schedule',
  'All Apps',
  'Multi Zones (SplitScreen)',
  'Multi Users',
];

/**
 * Pro Plus plan feature bullets — shown on the retention offer for Engage
 * users (Pro Plus is their cheaper downgrade tier).
 */
const PRO_PLUS_FEATURE_KEYS: string[] = [
  'Single Sign On (SAML)',
  'Dynamic Data Mapping',
  'Gated Website Auto Login',
  'Dashboard Logins',
  'Monitoring & Alert',
  'Security Logging',
];

/**
 * Free plan feature bullets — shown on the retention offer for Standard users
 * who picked "too expensive". Highlights what they keep on Free so the offer
 * doesn't read as a punishment.
 */
const FREE_FEATURE_KEYS: string[] = [
  'Up to 3 screens free',
  'Image, Video, Document',
  'Templates & Design Tool',
  'Playlist, Schedule',
  'Basic apps & widgets',
];

/** Backend PlanFeatures.plans entry — `plans[currency][monthly|yearly].price` */
type PlanPriceEntry = { price?: number };
type PlanFeatureNode = {
  alias?: string;
  plans?: Record<string, Record<string, PlanPriceEntry>>;
};
type AllPlanFeaturesResponse = {
  planFeatures?: { page?: { edges?: Array<{ node: PlanFeatureNode }> } };
};

/**
 * Find the price entry for a plan + currency + billing interval, matching the
 * lookup used by PlanComparisonTable so the retention card stays in sync with
 * the wizard pricing.
 */
const findPriceEntry = (
  plan: PlanFeatureNode | undefined,
  currency: string,
  isYearly: boolean,
): PlanPriceEntry | undefined => {
  const currencyPlans = plan?.plans?.[currency];
  if (!currencyPlans) return undefined;
  const matchToken = isYearly ? 'yearly' : 'monthly';
  const matchedKey = Object.keys(currencyPlans).find((k) => k.includes(matchToken));
  if (matchedKey) return currencyPlans[matchedKey];
  const fallbackKey = Object.keys(currencyPlans).find((k) => k.includes('monthly'));
  return fallbackKey ? currencyPlans[fallbackKey] : undefined;
};

/** Format a dollar amount like the wizard does: "$13.50" (always 2 decimals). */
const formatMoney = (amount: number, symbol: string): string => `${symbol}${amount.toFixed(2)}`;

type ReasonOption = { value: string; labelKey: string };

const REASON_OPTIONS: ReasonOption[] = [
  { value: 'too_expensive', labelKey: "It's too expensive" },
  { value: 'switch_plans', labelKey: 'I want to switch plans' },
  { value: 'understanding_product', labelKey: "I'm having too many technical issues" },
  { value: 'switched_solution', labelKey: 'I switched to another solution' },
  { value: 'missing_features', labelKey: 'A feature I need is missing' },
  { value: 'no_longer_need', labelKey: 'I no longer need digital signage' },
  { value: 'temporary_seasonal', labelKey: 'I only need it temporarily or seasonally' },
  { value: 'other', labelKey: 'Other' },
];

/**
 * Reasons that route to the "are you sure" confirm dialog before firing the
 * cancel mutation. The remaining reasons (too_expensive, understanding_product,
 * temporary_seasonal) route to their own retention steps instead.
 */
const CONFIRM_REASONS = new Set<string>([
  'switch_plans',
  'switched_solution',
  'missing_features',
  'no_longer_need',
  'other',
]);

/**
 * Native cancel-subscription survey. Captures reason + free-text feedback,
 * then fires the CANCEL_SUBSCRIPTION_V2 mutation. The backend
 * (changeSubscriptionService.cancelSubscriptionV2) persists `survey.questions`
 * into the UserSurvey collection when populated, so answers get saved.
 */
export function CancelSubscriptionDialog({
  open,
  onClose,
  subAccountId,
  onSwitchPlans,
  onSwitchToStandard,
  onSwitchToFree,
  onKeepSubscription,
}: Props) {
  const { t } = useTranslation();
  const apolloClient = useApolloClient();
  const [cancelSubscriptionMutation] = useMutation(CANCEL_SUBSCRIPTION_V2);
  // For the retention-step CTAs ("Submit support request", "Send to OptiSigns
  // team") — saves the captured note as a standalone survey row so the data
  // is preserved even when the user takes the retention offer instead of
  // cancelling. Backend can later hook these formTypes to push to HubSpot.
  const [submitSurvey] = useMutation<unknown, SubmitUserSurveyVariables>(SUBMIT_USER_SURVEY);
  // Set when the user clicks "Update subscription plan" → handed off to the
  // wizard. useSubscriptionWizard reads this on successful plan-update so the
  // userSurveys row is written only on a COMPLETED switch from the cancel flow.
  // Tag which cancel-dialog CTA the user took before the wizard / downgrade
  // flow runs. Read on successful completion in useSubscriptionWizard +
  // DowngradeComparisonDialog to write a cancelSwitchPlans / cancelDowngrade
  // userSurveys row. Cleared by closeDialog.
  const setCancelFlowSource = useBillingDialogStore((s) => s.setCancelFlowSource);
  // Notifies billing@optisigns.com (auto-creates a HubSpot ticket) when the
  // user takes the "Send to OptiSigns team" or "Submit support request"
  // retention path. Fire-and-forget — never blocks the thank-you screen.
  const [sendSupportRequest] = useMutation<unknown, SendCancellationSupportRequestVariables>(
    SEND_CANCELLATION_SUPPORT_REQUEST,
  );

  // Multi-step flow state. Reason + additionalInfo persist across step changes
  // so a selection in the survey step survives if the user back-navigates from
  // a retention step.
  const [step, setStep] = useState<Step>('survey');
  // The final "are you sure" gate renders as a separate dialog stacked on top
  // of the current step, rather than replacing the step content.
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Survey starts with no selection — the destructive "Continue cancellation"
  // button stays disabled until the user picks a reason.
  const [reason, setReason] = useState<string>('');
  const [additionalInfo, setAdditionalInfo] = useState<string>('');
  // Use-case description captured on the `special-pricing` step (seasonal /
  // low-usage retention path). Kept separate from `additionalInfo` because
  // they have different semantics — this one gets forwarded to sales.
  const [specialPricingNote, setSpecialPricingNote] = useState<string>('');
  // Issue description captured on the `having-trouble` step (trouble
  // understanding product → 1:1 support follow-up).
  const [troubleNote, setTroubleNote] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Inline error shown when the user clicks the destructive "Continue cancellation"
  // button on the survey step without selecting a reason. Surfacing this
  // post-click (instead of disabling the button) lets users see WHY nothing
  // happens — more discoverable than a tooltip on a greyed-out button.
  const [missingReasonError, setMissingReasonError] = useState(false);

  // Plan name for the Switch-to-Standard copy.
  const { planFeature, payment, currency } = useBillingData({ subAccountId, skip: !open });
  const planDisplayName = planFeature ? getPlanDisplayName(planFeature) : '';

  // Current billing interval — `payment.plan` is 'monthly' | 'yearly' |
  // 'yearly_freedevice'. Drives whether the Switch-to-Standard card shows the
  // "Billed annually" line: monthly subscribers would switch to monthly
  // Standard, so the annual price is just noise for them.
  const isMonthlyBilling = payment?.plan === 'monthly';
  const isYearlyBilling = !isMonthlyBilling;

  // Billing-period end date for the confirm-step copy. `period_end` (Unix
  // seconds) from getUpcomingSubscription is the authoritative Stripe-derived
  // value used by the Subscription page header; Apollo dedupes against it.
  const { data: upcomingData } = useQuery<{
    getUpcomingSubscription?: { upcoming?: { period_end?: number } };
  }>(GET_UPCOMING_SUBSCRIPTION, {
    variables: { isTrial: false, stripe_plan_id: '', subAccountId },
    skip: !open,
  });
  const periodEndUnix = upcomingData?.getUpcomingSubscription?.upcoming?.period_end;
  const periodEndDate =
    typeof periodEndUnix === 'number' ? formatDateMedium(new Date(periodEndUnix * 1000)) : '';

  // Live plan pricing — same source the SubscriptionWizard's PlanComparisonTable
  // uses, so the retention card and the plan-picker can never drift apart.
  const { data: allPlansData } = useQuery<AllPlanFeaturesResponse>(ALL_PLAN_FEATURES_QUERY, {
    fetchPolicy: 'cache-first',
    skip: !open,
  });
  const planNodes = allPlansData?.planFeatures?.page?.edges?.map((e) => e.node) ?? [];
  const findPlan = (alias: string): PlanFeatureNode | undefined =>
    planNodes.find((p) => p.alias === alias);

  // Each paid tier's retention offer points at the next cheaper tier:
  // Engage → Pro Plus, Pro Plus → Standard, Standard → Free.
  const retentionAlias =
    planFeature === PLAN_FEATURES.ENGAGE
      ? PLAN_FEATURES.PRO_PLUS
      : planFeature === PLAN_FEATURES.STANDARD
        ? PLAN_FEATURES.FREE_PLAN
        : PLAN_FEATURES.STANDARD;
  const isFreeRetention = retentionAlias === PLAN_FEATURES.FREE_PLAN;
  const retentionPlanNode = findPlan(retentionAlias);
  const retentionPriceEntry = findPriceEntry(retentionPlanNode, currency, isYearlyBilling);
  // Backend `plans[currency].yearly.price` is the annual total. Per-month
  // equivalent = annual / 12 (matches PlanComparisonTable display). Free has
  // no `plans` entry so the price falls to 0.
  const retentionRawPrice = retentionPriceEntry?.price ?? 0;
  const retentionMonthlyEquivalent = isYearlyBilling ? retentionRawPrice / 12 : retentionRawPrice;
  const retentionCurrencySymbol = getCurrencySymbol(currency);
  const retentionFeatureKeysByAlias: Record<string, string[]> = {
    [PLAN_FEATURES.PRO_PLUS]: PRO_PLUS_FEATURE_KEYS,
    [PLAN_FEATURES.STANDARD]: STANDARD_FEATURE_KEYS,
    [PLAN_FEATURES.FREE_PLAN]: FREE_FEATURE_KEYS,
  };
  const retentionOffer = {
    planName: getPlanDisplayName(retentionAlias),
    monthlyPrice: formatMoney(retentionMonthlyEquivalent, retentionCurrencySymbol),
    annualPrice: formatMoney(
      isYearlyBilling ? retentionRawPrice : retentionRawPrice * 12,
      retentionCurrencySymbol,
    ),
    featureKeys: retentionFeatureKeysByAlias[retentionAlias] ?? STANDARD_FEATURE_KEYS,
    isFree: isFreeRetention,
  };

  // Reset form when the dialog re-opens so the next user sees a fresh survey.
  // On close, also drop `confirmOpen` — the "are you sure" gate is a separate
  // nested <Dialog> that Radix portals independently, so it would otherwise
  // stay orphaned on screen after a successful cancel closes the outer dialog.
  useEffect(() => {
    if (open) {
      setReason('');
      setAdditionalInfo('');
      setSpecialPricingNote('');
      setTroubleNote('');
      setMissingReasonError(false);
      setStep('survey');
      setConfirmOpen(false);
    } else {
      setConfirmOpen(false);
    }
  }, [open]);

  /**
   * Click handler for the "Continue cancellation" buttons in the survey step. Routing:
   *
   *  - `too_expensive` → retention card pointing at the next cheaper tier
   *     (Engage → Pro Plus, Pro Plus → Standard, Standard → Free)
   *  - `temporary_seasonal` → "Consider a special pricing plan" retention offer
   *  - `understanding_product` → "Having trouble?" retention offer
   *  - Reasons in CONFIRM_REASONS → "are you sure" confirm dialog
   *  - Anything else → fire the cancel mutation immediately (their retention
   *     flows will land in later mockups)
   */
  /** Wrap setReason so picking any option clears the "missing reason" error. */
  const handleReasonChange = (next: string) => {
    setReason(next);
    if (next) setMissingReasonError(false);
  };

  const handleCancelClick = () => {
    if (!reason) {
      // No selection yet — surface an inline error instead of silently
      // doing nothing. Cleared when the user makes a choice (see setReason
      // wrapper).
      setMissingReasonError(true);
      return;
    }
    if (reason === 'too_expensive') {
      setStep('switch-to-standard');
      return;
    }
    if (reason === 'temporary_seasonal') {
      setStep('special-pricing');
      return;
    }
    if (reason === 'understanding_product') {
      setStep('having-trouble');
      return;
    }
    if (CONFIRM_REASONS.has(reason)) {
      setConfirmOpen(true);
      return;
    }
    handleConfirm();
  };

  /**
   * Retention-offer CTA — "Switch to {Pro Plus / Standard / Free}". Free routes
   * to onSwitchToFree (parent opens DowngradeSurveyDialog, same as the wizard's
   * "Select Free" button); the paid offers route to onSwitchToStandard (parent
   * jumps to the wizard's plan-switch Confirmation step). Both fall back to a
   * toast in standalone contexts (e.g. the dev catalog) where neither callback
   * is wired.
   */
  const handleSwitchToStandard = () => {
    if (isFreeRetention) {
      if (onSwitchToFree) {
        // Mark this downgrade as cancel-flow-originated so the downgrade
        // completion handler writes a `cancelDowngrade` userSurveys row.
        setCancelFlowSource('too_expensive');
        onSwitchToFree();
        return;
      }
    } else if (onSwitchToStandard) {
      // Same — mark for the wizard plan-update completion handler. Forward
      // `retentionAlias` so the wizard switches to the correct tier
      // (Engage → Pro Plus, Pro Plus → Standard), not a hardcoded Standard.
      setCancelFlowSource('too_expensive');
      onSwitchToStandard(retentionAlias);
      return;
    }
    toast.info(t('Switch to {{plan}} — coming soon', { plan: retentionOffer.planName }));
    onClose(false);
  };

  /**
   * Persist a retention-step survey (having-trouble / special-pricing) to the
   * userSurveys collection. Captures the user's reason + their free-text note
   * so the data is preserved even when they take the retention offer instead
   * of cancelling. Fire-and-forget — never blocks the thank-you screen.
   */
  const submitRetentionSurvey = (
    formType: string,
    noteKey: string,
    noteText: string,
    note: string,
  ) => {
    const questions: SubmitUserSurveyVariables['input']['questions'] = [];
    if (reason) {
      const selected = REASON_OPTIONS.find((r) => r.value === reason);
      questions.push({
        key: 'cancel_reason',
        text: 'Why are you cancelling?',
        answer: reason,
        answerLabel: selected?.labelKey ?? '',
      });
    }
    const trimmed = note.trim();
    if (trimmed) {
      questions.push({
        key: noteKey,
        text: noteText,
        answer: trimmed,
        answerLabel: trimmed,
      });
    }
    if (!questions.length) return;
    void submitSurvey({
      variables: {
        // All rows from the cancel-sub dialog share formId `cancelSubscription`
        // — formType identifies which step / outcome (cancel, retention CTA…).
        input: { formId: 'cancelSubscription', formVersion: 1, formType, questions },
      },
    }).catch((err) => {
      console.error(`submitUserSurvey (${formType}) failed`, err);
    });
  };

  /**
   * Fire the support-request email (replaces the legacy Zapier-on-TypeForm
   * flow that used to forward retention notes to billing@optisigns.com).
   * Backend looks up plan, Stripe customer, and screen count itself — we only
   * send `reason` + `note`. Failures are non-blocking: we toast and still
   * advance to the thank-you screen, since the survey row was already saved
   * by `submitRetentionSurvey` and we don't want to trap the user.
   */
  const notifyOptiSignsTeam = (reason: string, note: string) => {
    const trimmed = note.trim();
    if (!trimmed) return;
    void sendSupportRequest({
      variables: { reason, note: trimmed },
    }).catch((err) => {
      console.error(`sendCancellationSupportRequest (${reason}) failed`, err);
      toast.error(t("We couldn't send your request, but your response was saved."));
    });
  };

  /**
   * Submit handler for the special-pricing retention step ("Send to OptiSigns
   * team"). Persists the captured use-case as a userSurveys row with
   * `formType: 'specialPricing'` AND emails billing@optisigns.com so the team
   * can reach out about a flexible plan.
   */
  const handleRequestSpecialPricing = () => {
    submitRetentionSurvey(
      'specialPricing',
      'special_pricing_note',
      'Tell us a bit more about how you use OptiSigns',
      specialPricingNote,
    );
    notifyOptiSignsTeam('temporary_seasonal', specialPricingNote);
    setStep('thank-you');
  };

  /**
   * Submit handler for the having-trouble retention step ("Submit support
   * request"). Persists the captured issue as a userSurveys row with
   * `formType: 'havingTrouble'` AND emails billing@optisigns.com so support
   * can follow up.
   */
  const handleHavingTroubleSubmit = () => {
    submitRetentionSurvey('havingTrouble', 'trouble_note', 'What is the issue?', troubleNote);
    notifyOptiSignsTeam('understanding_product', troubleNote);
    setStep('thank-you');
  };

  // Keep the destructive footer button always actionable — clicking with no
  // reason set surfaces an inline error via `missingReasonError` instead of
  // a disabled-state tooltip.
  const { disabled: submitDisabled, disabledTooltip } = useDialogSubmitState({
    isSubmitting,
  });

  const buildQuestions = () => {
    const questions: { key: string; text: string; answer: string; answerLabel: string }[] = [];
    if (reason) {
      const selected = REASON_OPTIONS.find((r) => r.value === reason);
      questions.push({
        key: 'cancel_reason',
        text: 'Why are you cancelling?',
        answer: reason,
        answerLabel: selected?.labelKey ?? '',
      });
    }
    const additional = additionalInfo.trim();
    if (additional) {
      questions.push({
        key: 'additional_info',
        text: 'Tell us more about your decision',
        answer: additional,
        answerLabel: additional,
      });
    }
    // Retention-step notes — included only if the user typed something AND
    // ultimately went through with the cancellation. If they took the
    // retention offer instead (clicked "Submit support request" / "Send to
    // OptiSigns team"), handleConfirm never runs and these are skipped.
    const trouble = troubleNote.trim();
    if (trouble) {
      questions.push({
        key: 'trouble_note',
        text: 'What is the issue?',
        answer: trouble,
        answerLabel: trouble,
      });
    }
    const pricing = specialPricingNote.trim();
    if (pricing) {
      questions.push({
        key: 'special_pricing_note',
        text: 'Tell us a bit more about how you use OptiSigns',
        answer: pricing,
        answerLabel: pricing,
      });
    }
    return questions;
  };

  /**
   * Back-out path used by every "keep" button in the dialog. When the
   * parent provides `onKeepSubscription`, it owns the full unwind (close the
   * wrapping SubscriptionWizard + route back to the subscription page).
   * Otherwise we just close this dialog.
   */
  const handleKeep = () => {
    if (isSubmitting) return;
    if (onKeepSubscription) {
      onKeepSubscription();
      return;
    }
    onClose(false);
  };

  // "Continue cancellation" on the survey + retention steps advances the user
  // toward the confirm gate; the actual mutation only fires on the final
  // confirm step (labelled "Cancel subscription", destructive variant). Most
  // non-final buttons say "Continue cancellation" so the user always knows
  // they aren't done yet — and stays an outline button so the only red CTA is
  // the one that truly destroys.
  const primaryButtonLabel = t('Continue cancellation');
  // Picking "I want to switch plans" reframes the bottom CTA
  // from "Continue cancellation" into "Update subscription plan", handing off
  // to the plan-change wizard via onSwitchPlans. The "keep" button is
  // unaffected.
  const isSwitchingPlans = reason === 'switch_plans';
  const primaryActionLabel = isSwitchingPlans ? t('Update subscription plan') : primaryButtonLabel;
  const primaryActionHandler = () => {
    if (!reason) {
      setMissingReasonError(true);
      return;
    }
    if (isSwitchingPlans && onSwitchPlans) {
      // Flag the wizard session as originating from the cancel-sub flow.
      // useSubscriptionWizard reads this on successful plan-update and writes
      // a `cancelSwitchPlans` userSurveys row — so we only record plan
      // switches that actually completed AND started from cancel (not from
      // the normal Subscription page). Cleared by closeDialog.
      setCancelFlowSource('switch_plans');
      onSwitchPlans();
      return;
    }
    handleCancelClick();
  };

  const handleConfirm = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const variables: Record<string, unknown> = {
        survey: {
          formId: 'cancelSubscription',
          formVersion: 1,
          formType: 'cancelSubscription',
          questions: buildQuestions(),
        },
      };
      if (subAccountId) variables.subAccountId = subAccountId;

      const result = await cancelSubscriptionMutation({ variables });
      const response = (
        result.data as Record<string, { status?: boolean; message?: string } | undefined>
      )?.cancelSubscriptionV2;

      if (!response?.status) {
        console.error('cancelSubscriptionV2 failed', response);
        toast.error(response?.message ?? t('Failed to cancel subscription'));
        return;
      }

      apolloClient.refetchQueries({
        include: [
          'GetUpcomingSubscription',
          'GetSubscriptionInfoStripe',
          ...(subAccountId ? ['GetSubAccountInfo', 'GetSubAccountList'] : ['GetCompanyInfo']),
        ],
      });

      onClose(true);
    } catch (err) {
      console.error('cancelSubscriptionV2 threw', err);
      toast.error((err as { message?: string })?.message ?? t('Failed to cancel subscription'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Placeholder for the free-text box, tailored to the selected reason so the
  // prompt is specific. Every t() call is a static literal (keyed by reason,
  // no dynamic key construction); reasons not listed fall back to the generic
  // prompt.
  const reasonPlaceholders: Record<string, string> = {
    switched_solution: t('eg. solution you switched to and why'),
    missing_features: t('eg. feature(s) you wanted to see'),
    other: t('Tell us more about your reason'),
  };
  const additionalInfoPlaceholder = reasonPlaceholders[reason] ?? t('eg. reasons for cancel');
  // Hide the free-text input until the user picks a reason that benefits
  // from elaboration (switched, missing feature, or other).
  const showAdditionalInfo =
    reason === 'switched_solution' || reason === 'missing_features' || reason === 'other';

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !isSubmitting) onClose(false);
      }}
    >
      <DialogContent
        className="flex max-h-[85vh] max-w-lg flex-col overflow-hidden"
        data-testid="cancel-subscription-dialog"
      >
        {step === 'switch-to-standard' ? (
          /* Retention offer — shown when the user picks "too_expensive" and is
              on a plan above Standard. Engage users are offered Pro Plus; Pro
              Plus users are offered Standard (see retentionOffer). The cancel
              path still works — this is a save attempt first. */
          <div
            className="flex-1 space-y-4 overflow-y-auto px-1 pb-1"
            data-testid="cancel-sub-switch-to-standard-step"
          >
            <div className="space-y-1.5 pr-8 pt-4">
              <DialogTitle>
                {t('Switch to {{plan}}', { plan: retentionOffer.planName })}
              </DialogTitle>
              <DialogDescription>
                {planDisplayName
                  ? t(
                      "You're currently on {{currentPlan}}. Switch to {{targetPlan}} to lower your cost while keeping your screens active.",
                      { currentPlan: planDisplayName, targetPlan: retentionOffer.planName },
                    )
                  : t(
                      'Switch to {{targetPlan}} to lower your cost while keeping your screens active.',
                      { targetPlan: retentionOffer.planName },
                    )}
              </DialogDescription>
            </div>

            <div className="rounded-lg border bg-card p-4 shadow-sm">
              {/* Price block */}
              <div className="text-center">
                <div className="text-3xl font-semibold text-primary">
                  {retentionOffer.isFree ? t('Free') : retentionOffer.monthlyPrice}
                </div>
                {!retentionOffer.isFree && (
                  <div className="mt-1 text-xs text-muted-foreground">{t('screens/month')}</div>
                )}
                {/* Annual price hidden for monthly subscribers and for the Free
                    offer — they'd switch to a monthly / no-charge plan, so the
                    annual figure is just noise. */}
                {!isMonthlyBilling && !retentionOffer.isFree && (
                  <div className="mt-2 text-xs font-medium text-muted-foreground">
                    {t('{{price}} Billed annually', { price: retentionOffer.annualPrice })}
                  </div>
                )}
              </div>

              {/* Feature bullets */}
              <ul className="mt-4 space-y-2">
                {retentionOffer.featureKeys.map((featureKey) => (
                  <li key={featureKey} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                    <span className="text-sm">{t(featureKey)}</span>
                  </li>
                ))}
              </ul>

              <Button
                className="mt-4 w-full"
                onClick={handleSwitchToStandard}
                disabled={isSubmitting}
                data-testid="switch-to-standard-btn"
              >
                {t('Switch to {{plan}}', { plan: retentionOffer.planName })}
              </Button>

              {/* Disclaimer — the retention plan is a lower tier, so the user
                  may lose plan-specific features. Both plan names interpolated. */}
              <p className="mt-3 text-center text-xs text-muted-foreground">
                {planDisplayName
                  ? t('Some {{currentPlan}} features may not be available on {{targetPlan}}.', {
                      currentPlan: planDisplayName,
                      targetPlan: retentionOffer.planName,
                    })
                  : t('Some features may not be available on {{targetPlan}}.', {
                      targetPlan: retentionOffer.planName,
                    })}
              </p>
            </div>

            {/* Declining the retention offer → still route through the "are
                you sure" confirm gate before firing the cancel mutation.
                Outline variant; the destructive action lives one step later. */}
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setConfirmOpen(true)}
              disabled={isSubmitting}
              data-testid="cancel-plan-btn"
            >
              {t('No thanks, continue cancellation')}
            </Button>
          </div>
        ) : step === 'special-pricing' ? (
          /* Special-pricing retention offer — shown when the user picks
              "only seasonal / don't use it enough". Captures a free-text
              use-case description for the sales team to follow up with a
              custom plan. */
          <div
            className="flex-1 space-y-4 overflow-y-auto px-1 pb-1"
            data-testid="cancel-sub-special-pricing-step"
          >
            <div className="space-y-1.5 pr-8 pt-4">
              <DialogTitle>{t('Only need OptiSigns part time?')}</DialogTitle>
              <DialogDescription>
                {t(
                  "Tell us how you use OptiSigns, and we'll see if a flexible plan is available for your use case.",
                )}
              </DialogDescription>
            </div>

            {/* Contextual help tip card — seasonal users can park devices On
                Hold (no license consumed) instead of cancelling outright.
                Styled as a soft branded callout so the alternative actually
                competes with the textarea below for attention. */}
            <a
              href={ON_HOLD_HELP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="special-pricing-on-hold-link"
              className="group flex cursor-pointer items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 p-3 transition-colors hover:border-primary/50 hover:bg-primary/10"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/15 text-primary">
                <PauseCircle className="h-5 w-5" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground">
                  {t('Pause your devices instead')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t(
                    'OnHold Devices lets you pause your subscription without removing your screens.',
                  )}
                </p>
              </div>
              <ArrowRight
                className="h-4 w-4 shrink-0 text-primary transition-transform group-hover:translate-x-0.5"
                aria-hidden
              />
            </a>

            <div className="flex items-center gap-3">
              <div className="flex-1 border-t border-border" />
              <span className="text-xs font-medium text-muted-foreground">{t('OR')}</span>
              <div className="flex-1 border-t border-border" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="special-pricing-note" className="text-sm font-semibold">
                {t('Tell us a bit more about how you use OptiSigns')}
              </Label>
              <Textarea
                id="special-pricing-note"
                data-testid="special-pricing-note-textarea"
                placeholder={t('eg. seasonal pop-up store, conference booth, holiday menu boards…')}
                value={specialPricingNote}
                onChange={(e) =>
                  setSpecialPricingNote(e.target.value.slice(0, ADDITIONAL_INFO_MAX))
                }
                disabled={isSubmitting}
                maxLength={ADDITIONAL_INFO_MAX}
                rows={6}
                className="resize-none"
              />
              <div className="text-right text-xs text-muted-foreground">
                {specialPricingNote.length}/{ADDITIONAL_INFO_MAX}
              </div>
            </div>

            <div className="flex flex-col gap-2 pt-2">
              <Button
                className="w-full"
                onClick={handleRequestSpecialPricing}
                disabled={isSubmitting}
                data-testid="special-pricing-yes-btn"
              >
                {t('Send to OptiSigns team')}
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setConfirmOpen(true)}
                disabled={isSubmitting}
                data-testid="cancel-plan-btn"
              >
                {t('No thanks, continue cancellation')}
              </Button>
            </div>
          </div>
        ) : step === 'having-trouble' ? (
          /* Having-trouble retention offer — shown when the user picks
              "Trouble understanding product". Captures a free-text note
              and offers a 1:1 follow-up from a support specialist. */
          <div
            className="flex-1 space-y-4 overflow-y-auto px-1 pb-1"
            data-testid="cancel-sub-having-trouble-step"
          >
            <div className="space-y-1.5 pr-8 pt-4">
              <DialogTitle>{t('Having trouble? Let us help')}</DialogTitle>
              <DialogDescription>
                {t('Tell us what is not working as expected, and our team will reach out to help.')}
              </DialogDescription>
            </div>

            <div className="space-y-2">
              <Label htmlFor="trouble-note" className="text-sm font-semibold">
                {t('What is the issue?')}
              </Label>
              <Textarea
                id="trouble-note"
                data-testid="trouble-note-textarea"
                placeholder={t(
                  'eg. cannot pair my screen, content not playing, scheduling unclear…',
                )}
                value={troubleNote}
                onChange={(e) => setTroubleNote(e.target.value.slice(0, ADDITIONAL_INFO_MAX))}
                disabled={isSubmitting}
                maxLength={ADDITIONAL_INFO_MAX}
                rows={6}
                className="resize-none"
              />
              <div className="text-right text-xs text-muted-foreground">
                {troubleNote.length}/{ADDITIONAL_INFO_MAX}
              </div>
            </div>

            <div className="flex flex-col gap-2 pt-2">
              <Button
                className="w-full"
                onClick={handleHavingTroubleSubmit}
                disabled={isSubmitting}
                data-testid="having-trouble-submit-btn"
              >
                {t('Submit support request')}
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setConfirmOpen(true)}
                disabled={isSubmitting}
                data-testid="cancel-plan-btn"
              >
                {t('No thanks, continue cancellation')}
              </Button>
            </div>
          </div>
        ) : step === 'thank-you' ? (
          /* Shared post-submit acknowledgement for the two "request a
              follow-up" retention paths. The message is keyed off `reason`:
              `temporary_seasonal` arrives via special-pricing, every other
              path via having-trouble. "Done" tears down the wizard stack just
              like the keep buttons. */
          <div className="space-y-4" data-testid="cancel-sub-thank-you-step">
            <div className="space-y-1.5 pr-8">
              <DialogTitle>{t('Thank you!')}</DialogTitle>
              <DialogDescription>
                {reason === 'temporary_seasonal'
                  ? t(
                      'No changes have been made to your subscription. Our team will review your use case and reach out as soon as possible.',
                    )
                  : t(
                      'No changes have been made to your subscription. Our team will reach out as soon as possible to help.',
                    )}
              </DialogDescription>
            </div>

            <div className="flex justify-end pt-2">
              <Button onClick={handleKeep} disabled={isSubmitting} data-testid="thank-you-done-btn">
                {t('Done')}
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Survey step — reason picker (radio list). The wrapping div +
                `pr-8` reserves clearance for DialogContent's absolute X in
                the top-right corner (rather than using DialogHeader, which
                vertically centers an inline X with the title block).
                pt-4 adds breathing room above the header. The X close button
                is absolutely positioned in DialogContent, so it stays pinned
                top-right regardless of this padding. */}
            <div className="space-y-1.5 pr-8 pt-4">
              <DialogTitle>{t('Why are you cancelling?')}</DialogTitle>
              <DialogDescription>
                {t('We use this information to improve OptiSigns.')}
              </DialogDescription>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto px-1 pb-1 pt-4">
              {missingReasonError && (
                <p
                  role="alert"
                  className="text-sm font-medium text-destructive"
                  data-testid="cancel-sub-missing-reason-error"
                >
                  {t('Please select a reason before cancelling.')}
                </p>
              )}
              <RadioGroup value={reason} onValueChange={handleReasonChange} className="gap-1">
                {REASON_OPTIONS.map((opt) => {
                  const id = `cancel-reason-${opt.value}`;
                  const isSelected = reason === opt.value;
                  return (
                    <Label
                      key={opt.value}
                      htmlFor={id}
                      data-state={isSelected ? 'checked' : 'unchecked'}
                      className={cn(
                        'flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm font-normal transition-colors hover:bg-accent',
                        isSelected && 'bg-secondary hover:bg-secondary',
                      )}
                    >
                      <RadioGroupItem
                        value={opt.value}
                        id={id}
                        data-testid={`cancel-reason-${opt.value}`}
                        className="h-5 w-5 border border-input shadow-none data-[state=checked]:border-[5px] data-[state=checked]:border-primary [&_svg]:hidden"
                      />
                      <span>{t(opt.labelKey)}</span>
                    </Label>
                  );
                })}
              </RadioGroup>

              {showAdditionalInfo && (
                <div className="space-y-2">
                  <Label htmlFor="cancel-additional-info" className="sr-only">
                    {t('Tell us more about your decision')}
                  </Label>
                  <Input
                    id="cancel-additional-info"
                    data-testid="cancel-additional-info-input"
                    placeholder={additionalInfoPlaceholder}
                    value={additionalInfo}
                    onChange={(e) => setAdditionalInfo(e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 pt-6">
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleKeep}
                  disabled={isSubmitting}
                  data-testid="keep-current-plan-btn"
                >
                  {t('Keep current plan')}
                </Button>
                <Button
                  variant="outline"
                  onClick={primaryActionHandler}
                  disabled={submitDisabled}
                  disabledTooltip={disabledTooltip}
                  data-testid="cancel-plan-btn"
                >
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {primaryActionLabel}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>

      {/* Final "are you sure" gate — a separate dialog stacked on top of the
          current step (survey / retention offer), so the underlying step
          stays visible behind it instead of being replaced. The destructive
          mutation only fires from "Cancel subscription" here. */}
      <Dialog
        open={confirmOpen}
        onOpenChange={(o) => {
          if (!o && !isSubmitting) setConfirmOpen(false);
        }}
      >
        <DialogContent className="max-w-lg" data-testid="cancel-sub-confirm-dialog">
          <div className="space-y-8 py-2">
            <div className="space-y-1.5 pr-8">
              <DialogTitle>{t('Are you sure you want to cancel your subscription?')}</DialogTitle>
              <DialogDescription>
                {periodEndDate
                  ? t(
                      'Your subscription will stay active until {{date}}. After that, recurring billing will stop.',
                      { date: periodEndDate },
                    )
                  : t(
                      'Your subscription will stay active until the end of your current billing period. After that, recurring billing will stop.',
                    )}
              </DialogDescription>
            </div>

            <div className="flex flex-col gap-2 pt-2">
              <Button
                variant="destructive"
                className="w-full"
                onClick={handleConfirm}
                disabled={submitDisabled}
                disabledTooltip={disabledTooltip}
                data-testid="confirm-cancel-subscription-btn"
              >
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('Cancel subscription')}
              </Button>
              <Button
                variant="ghost"
                className="w-full"
                onClick={handleKeep}
                disabled={isSubmitting}
                data-testid="keep-my-current-subscription-btn"
              >
                {t('Keep my current subscription')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
